import React, { useCallback, useMemo } from 'react';

import {
    Action,
    type ActionCellProps,
    DataGridProps,
    GetReportIcon,
    Icon,
    NotificationManager,
    UtilityMoreIcon,
} from '@placer-ui/components';
import {
    ColumnsMatching,
    RequestResultsStatus,
    ResolverErrorCodesEnum,
    SupportRequestResultsModalTableRow,
    SupportRequestResultsResponse,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import {
    getAddressTypeFromColumnMapping,
    getEntityReportLink,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/utils';
import { ValidationType } from '@placer-ui/components/dist/data-grid/data-grid-types';
import {
    DEFAULT_EMPTY_VALUE,
    formatStatusFromEnum,
    useSetJobResultsQueryData,
} from 'features/offline-reports/support-requests/components/support-requests-modals/utils/utils';
import { getFeatureFlags } from 'core/flow-control';
import { useAtomValue, useSetAtom } from 'jotai';
import {
    mappedFileDataStore,
    resultsTableEditedStore,
    supportRequestAddressTypeStore,
} from '../../../../../../../store/property-store';
import {
    getFlagsMap,
    getVenueFlagInfoPublicPOITooltip,
} from 'ui-components/venue-status-indicator/v2/venue-status-indicator-v2';
import { AskForReview } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/components/ask-for-review/ask-for-review';
import {
    AskForReviewOnSubmitProps,
    EAskFowReviewFormSectionType,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/components/ask-for-review/types';
import { useAskForReviewOptions } from './components/ask-for-review/use-ask-for-review-flow-options';
import {
    basePropertyDataForChallenge,
    getChallengeAddressData,
    getChallengeStatus,
    resetChallengeData,
} from './components/ask-for-review/utils';
import startCase from 'lodash/startCase';

const getEmptyStateConfig = () =>
    ({
        valueFormatter: ({ value }) => value ?? DEFAULT_EMPTY_VALUE,
    } as DataGridProps<SupportRequestResultsModalTableRow>['columns'][0]);

const getLoadingConfig = (isSubmitting: boolean) =>
    ({
        valueFormatter: ({ value, data }) =>
            data?.isLoading ? DEFAULT_EMPTY_VALUE : value ?? DEFAULT_EMPTY_VALUE,
        editable: ({ data }) => {
            return !data?.isLoading && !isSubmitting;
        },
    } as DataGridProps<SupportRequestResultsModalTableRow>['columns'][0]);

const getValidationConfig = (validation: ResolverErrorCodesEnum[]) => {
    return {
        indicator: 'Cell',
        validator: ({ rowData }) => {
            return validation.includes(rowData.field_error_code);
        },
        getTooltipText: ({ data }) => {
            return data?.error_message;
        },
    } as ValidationType<SupportRequestResultsModalTableRow>;
};

const getStatusTooltip = (
    status: RequestResultsStatus | undefined,
    errorMessage: string | undefined,
) => {
    const config: Record<RequestResultsStatus, string> = {
        MissingInfo: errorMessage ?? '',
        Edited: 'This row has been edited',
        Available: 'Property available with instant access to reports',
        ProcessingFailed: 'Re-check coverage to process this property again',
        ReviewRequired: 'Submit for manual review by Placer’s support team',
        SensitiveLocation: 'Property is located in an area that violates Placer’s policy',
    };

    return status ? config[status] : '';
};

export const useTableConfig = ({ isSubmitting }: { isSubmitting: boolean }) => {
    const notificationManager = NotificationManager.getInstance();
    const setJobResultsQueryData = useSetJobResultsQueryData();
    const { getOptions } = useAskForReviewOptions();
    const { enable_submit_feedback_resolver, enable_support_data_validation_nearby_reason_ff } =
        getFeatureFlags();
    const supportRequestAddressType = useAtomValue(supportRequestAddressTypeStore);
    const setIsDataEdited = useSetAtom(resultsTableEditedStore);
    const setMappedFileData = useSetAtom(mappedFileDataStore);

    const emptyStateConfig = useMemo(() => getEmptyStateConfig(), []);
    const loadingConfig = useMemo(() => getLoadingConfig(isSubmitting), [isSubmitting]);

    const getDefaultColumnsConfig = useCallback(
        (isLoadable?: boolean) => ({
            ...emptyStateConfig,
            ...(isLoadable ? loadingConfig : {}),
        }),
        [emptyStateConfig, loadingConfig],
    );

    const getColumnConfig = useCallback(
        (
            columnsMatching: ColumnsMatching | undefined = {},
        ): DataGridProps<SupportRequestResultsModalTableRow>['columns'] => {
            // TODO - need take from original mapping
            const addressType = getAddressTypeFromColumnMapping(columnsMatching);

            return [
                {
                    field: 'lineId',
                    headerName: '#',
                    width: 45,
                },
                {
                    field: 'entity_name',
                    headerName: 'Name',
                    minWidth: 180,
                    flex: 1,
                    ...getDefaultColumnsConfig(),
                },
                {
                    field: 'entity_address',
                    headerName: 'Address',
                    minWidth: 180,
                    flex: 1,
                    ...getDefaultColumnsConfig(),
                },
                {
                    field: 'name',
                    headerName: 'Name (Source)',
                    minWidth: 180,
                    flex: 1,
                    ...getDefaultColumnsConfig(true),
                    validation: getValidationConfig([ResolverErrorCodesEnum.MissingName]),
                },
                ...(addressType === 'formatted_address'
                    ? ([
                          {
                              field: 'formatted_address',
                              headerName: 'Address (Source)',
                              minWidth: 160,
                              editable: true,
                              ...getDefaultColumnsConfig(true),
                              validation: getValidationConfig([
                                  ResolverErrorCodesEnum.MissingFormattedAddress,
                                  ResolverErrorCodesEnum.MissingSomeAddressField,
                                  ResolverErrorCodesEnum.InvalidAddress,
                              ]),
                          },
                      ] as DataGridProps<SupportRequestResultsModalTableRow>['columns'])
                    : []),
                ...(addressType === 'separate_columns'
                    ? ([
                          {
                              field: 'street',
                              headerName: 'Street (Source)',
                              minWidth: 180,
                              flex: 1,
                              editable: true,
                              ...getDefaultColumnsConfig(true),
                              validation: getValidationConfig([
                                  ResolverErrorCodesEnum.MissingStreet,
                                  ResolverErrorCodesEnum.MissingSomeAddressField,
                              ]),
                          },
                          {
                              field: 'city',
                              headerName: 'City (Source)',
                              minWidth: 180,
                              flex: 1,
                              editable: true,
                              ...getDefaultColumnsConfig(true),
                              validation: getValidationConfig([
                                  ResolverErrorCodesEnum.MissingCity,
                                  ResolverErrorCodesEnum.MissingSomeAddressField,
                              ]),
                          },
                          {
                              field: 'state',
                              headerName: 'State (Source)',
                              minWidth: 180,
                              flex: 1,
                              editable: true,
                              ...getDefaultColumnsConfig(true),
                              validation: getValidationConfig([
                                  ResolverErrorCodesEnum.InvalidState,
                                  ResolverErrorCodesEnum.MissingState,
                                  ResolverErrorCodesEnum.MissingSomeAddressField,
                              ]),
                          },
                      ] as DataGridProps<SupportRequestResultsModalTableRow>['columns'])
                    : []),
                ...(addressType === 'lat_lng'
                    ? ([
                          {
                              field: 'lat',
                              headerName: 'Lat (Source)',
                              flex: 1,
                              minWidth: 160,
                              ...getDefaultColumnsConfig(true),
                              numericConfigurations: {
                                  enableShortening: false,
                                  shouldShowTextWhenNoValue: true,
                              },
                              validation: getValidationConfig([
                                  ResolverErrorCodesEnum.InvalidLat,
                                  ResolverErrorCodesEnum.MissingLat,
                                  ResolverErrorCodesEnum.MissingSomeAddressField,
                              ]),
                          },
                          {
                              field: 'lng',
                              headerName: 'Long (Source)',
                              flex: 1,
                              minWidth: 160,
                              ...getDefaultColumnsConfig(true),
                              numericConfigurations: {
                                  enableShortening: false,
                              },
                              validation: getValidationConfig([
                                  ResolverErrorCodesEnum.InvalidLng,
                                  ResolverErrorCodesEnum.MissingLng,
                                  ResolverErrorCodesEnum.MissingSomeAddressField,
                              ]),
                          },
                      ] as DataGridProps<SupportRequestResultsModalTableRow>['columns'])
                    : []),

                ...(columnsMatching?.zip_code
                    ? ([
                          {
                              field: 'zip_code',
                              headerName: 'Zip Code (Source)',
                              cellDataType: 'text',
                              minWidth: 190,
                              flex: 1,
                              numericConfigurations: {
                                  enableShortening: false,
                              },
                              ...getDefaultColumnsConfig(true),
                              validation: getValidationConfig([
                                  ResolverErrorCodesEnum.InvalidZipCode,
                              ]),
                          },
                      ] as DataGridProps<SupportRequestResultsModalTableRow>['columns'])
                    : []),
                {
                    field: 'report_type_message',
                    headerName: 'Report Available',
                    maxWidth: 190,
                    ...getDefaultColumnsConfig(),
                },
                ...(enable_support_data_validation_nearby_reason_ff
                    ? ([
                          {
                              field: 'main_flag_reason',
                              ...getDefaultColumnsConfig(),
                              valueFormatter: ({ data }) => {
                                  const flag =
                                      data?.main_flag_reason === 'unavailable'
                                          ? 'unknown_flag'
                                          : data?.main_flag_reason;

                                  const isEnabled =
                                      (data?.status === 'Available' ||
                                          data?.challenge_status_override === 'ReviewRequired') &&
                                      flag;

                                  return isEnabled
                                      ? getFlagsMap()[flag] ?? DEFAULT_EMPTY_VALUE
                                      : DEFAULT_EMPTY_VALUE;
                              },
                              getTooltipText: ({ data }) => {
                                  const flag =
                                      data?.main_flag_reason === 'unavailable'
                                          ? 'unknown_flag'
                                          : data?.main_flag_reason;

                                  const isEnabled =
                                      (data?.status === 'Available' ||
                                          data?.challenge_status_override === 'ReviewRequired') &&
                                      flag;

                                  return isEnabled
                                      ? getVenueFlagInfoPublicPOITooltip(flag, data?.entity_name)
                                      : '';
                              },
                              headerName: 'Availability Reason',
                              minWidth: 220,
                              flex: 1,
                          },
                      ] as DataGridProps<SupportRequestResultsModalTableRow>['columns'])
                    : []),
                {
                    field: 'status',
                    headerName: 'Status',
                    cellType: 'label',
                    pinned: 'right',
                    ...getDefaultColumnsConfig(),
                    valueFormatter: ({ value, data }) => {
                        return formatStatusFromEnum({
                            status: value,
                            isEdited: data?.isEdited,
                        });
                    },
                    flex: 1,
                    minWidth: 207,
                    getTooltipText: ({ data }) => {
                        return getStatusTooltip(data?.status, data?.error_message);
                    },
                    customCellGlobalProps: {
                        textToLabelColor: {
                            Available: 'green',
                            MissingInfo: 'yellow',
                            SensitiveLocation: 'brown',
                            ReviewRequired: 'blue',
                            ProcessingFailed: 'pink',
                            Edited: 'gray',
                        },
                    },
                },
                {
                    field: 'actions',
                    headerName: '',
                    cellType: 'action',
                    pinned: 'right',
                    maxWidth: 120,
                },
            ];
        },
        [enable_support_data_validation_nearby_reason_ff, getDefaultColumnsConfig],
    );

    const getErrorMessage = (field_error: number) => {
        const missing = 'Provide the';
        const invalid = 'Verify the';

        return {
            [ResolverErrorCodesEnum.MissingName]: `${missing} property name`,
            [ResolverErrorCodesEnum.MissingLat]: `${missing} latitude coordinate`,
            [ResolverErrorCodesEnum.MissingLng]: `${missing} longitude coordinate`,
            [ResolverErrorCodesEnum.MissingCity]: `${missing} city`,
            [ResolverErrorCodesEnum.MissingState]: `${missing} state`,
            [ResolverErrorCodesEnum.MissingStreet]: `${missing} street`,
            [ResolverErrorCodesEnum.MissingFormattedAddress]: `${missing} address`,
            [ResolverErrorCodesEnum.MissingSomeAddressField]: `${missing} address`,
            [ResolverErrorCodesEnum.InvalidLat]: `${invalid} latitude coordinate`,
            [ResolverErrorCodesEnum.InvalidLng]: `${invalid} longitude coordinate`,
            [ResolverErrorCodesEnum.InvalidZipCode]: `${invalid} zip code`,
            [ResolverErrorCodesEnum.InvalidState]: `${invalid} state`,
            [ResolverErrorCodesEnum.InvalidAddress]: `${invalid} address`,
        }[field_error ?? 0];
    };

    const handleOpenReport = useCallback((item: SupportRequestResultsResponse) => {
        if (item.entity_id && item.entity_type) {
            const link = getEntityReportLink(item);

            window.open(link, '_blank');
        }
    }, []);

    const handleDeleteRow = useCallback(
        (item: SupportRequestResultsResponse) => {
            setJobResultsQueryData((oldData) => {
                if (!oldData) return oldData;
                return oldData.filter(({ id }) => id !== item.id);
            });
            setMappedFileData((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    data: prev.data.filter(({ id }) => id !== item.id),
                };
            });
            setIsDataEdited(true);
        },
        [setJobResultsQueryData, setMappedFileData, setIsDataEdited],
    );

    const handleSubmitAskForReview = useCallback(
        ({ type, data, item }: AskForReviewOnSubmitProps) => {
            const challengeStatusOverride = getChallengeStatus(type, data);

            const challengeDataByType: Record<
                string,
                Partial<Omit<SupportRequestResultsModalTableRow, 'actions'>>
            > = {
                [EAskFowReviewFormSectionType.UpdatePropertyName]: {
                    challenge_property_name: data.name,
                    challenge_status_override: 'ReviewRequired',
                },
                [EAskFowReviewFormSectionType.UpdatePropertyAddress]: {
                    challenge_property_address: data.address,
                    challenge_status_override: 'ReviewRequired',
                },
                [EAskFowReviewFormSectionType.NotMyProperty]: {
                    challenge_selected_alt: data.selected_alt,
                    name: data.name,
                    zip_code: data.zip_code,
                    challenge_googlemap_link: data.google_map_link,
                    ...getChallengeAddressData(supportRequestAddressType!, data),
                    ...(data.isDirty
                        ? {
                              isEdited: true,
                              challenge_type: undefined,
                          }
                        : {
                              ...basePropertyDataForChallenge,
                              challenge_status_override: 'ReviewRequired',
                          }),
                },
                [EAskFowReviewFormSectionType.ReasonForNearby]: {
                    ...(data.isDirty
                        ? {
                              challenge_note: data.note,
                          }
                        : {
                              challenge_type: undefined,
                          }),
                },
            };

            const challengeData: Partial<Omit<SupportRequestResultsModalTableRow, 'actions'>> = {
                challenge_type: type,
                challenge_status_override: challengeStatusOverride,
                ...challengeDataByType[type],
            };

            setJobResultsQueryData((oldData) => {
                if (!oldData) return oldData;
                const itemIndex = oldData.findIndex(({ id }) => id === item.id);

                if (itemIndex === -1) return oldData;

                oldData[itemIndex] = {
                    ...oldData[itemIndex],
                    ...challengeData,
                };

                return oldData;
            });
            setMappedFileData((prev) => {
                if (!prev) return prev;

                const itemIndex = prev?.data.findIndex(({ id }) => id === item.id);

                if (itemIndex === -1) return prev;

                const { status, ...rest } = challengeData;

                prev.data[itemIndex] = {
                    ...prev.data[itemIndex],
                    ...rest,
                };

                return {
                    ...prev,
                };
            });
            setIsDataEdited(true);

            if (challengeStatusOverride) {
                notificationManager.addNotification({
                    type: 'toast',
                    title: `Request received, status set to ${startCase(challengeStatusOverride)}.
Support will also review this property.`,
                    id: 'request-received-success',
                    testId: 'request-received-success',
                    icon: 'comments',
                });
            }
        },
        [
            notificationManager,
            setIsDataEdited,
            setJobResultsQueryData,
            setMappedFileData,
            supportRequestAddressType,
        ],
    );

    const getTableRows = useCallback(
        (data: SupportRequestResultsModalTableRow[]): SupportRequestResultsModalTableRow[] => {
            return data.map(
                (item, index): SupportRequestResultsModalTableRow => ({
                    ...resetChallengeData,
                    ...item,
                    status: item.challenge_status_override ?? item.status,
                    lineId: `${index + 1}`,
                    error_message: getErrorMessage(item.field_error_code),
                    link_to_report: getEntityReportLink(item),
                    main_flag_reason: item.flag_reasons?.[0],
                    report_type_message:
                        item.report_type === 'Property'
                            ? 'Property Report'
                            : item.report_type === 'Nearby'
                            ? 'Nearby Activity Report'
                            : null,
                    actions: {
                        Actions: [
                            ...(item.status === 'Available'
                                ? ([
                                      {
                                          Icon: <GetReportIcon />,
                                          onClick: () => handleOpenReport(item),
                                          actionType: 'icon-button',
                                          testId: 'data-validation-open-report',
                                          variant: 'secondary-blue',
                                          tooltip: 'Open Report',
                                      },
                                  ] as Action[])
                                : []),
                            {
                                Icon: <UtilityMoreIcon />,
                                actionType: 'icon-button',
                                testId: 'data-validation-more-action',
                                variant: 'ghost',
                                tooltip: 'More Action',
                                menuItems: [
                                    ...(!item.isEdited &&
                                    enable_submit_feedback_resolver &&
                                    !item.challenge_status_override &&
                                    getOptions({
                                        status: item.challenge_status_override ?? item.status,
                                        entityId: item.entity_id,
                                        flag: item.flag_reasons?.[0],
                                    }).length
                                        ? [
                                              {
                                                  label: 'Ask For Review',
                                                  value: 'ask-for-review',
                                                  testId: 'ask-for-review',
                                                  LeftComponent: <Icon type="Chats" />,
                                                  inlineFormMode: {
                                                      children: (
                                                          <AskForReview
                                                              onSubmit={handleSubmitAskForReview}
                                                              item={item}
                                                          />
                                                      ),
                                                  },
                                              },
                                          ]
                                        : []),
                                    {
                                        value: 'Delete',
                                        LeftComponent: <Icon type="Trash" />,
                                        tooltip:
                                            'Remove a property row from the data validation list',
                                        label: 'Delete',
                                        onClick: () => handleDeleteRow(item),
                                    },
                                ],
                            },
                        ],
                    } as ActionCellProps,
                    ...(item.challenge_type === EAskFowReviewFormSectionType.NotMyProperty &&
                    item.challenge_selected_alt === -1
                        ? basePropertyDataForChallenge
                        : {}),
                }),
            );
        },
        [
            enable_submit_feedback_resolver,
            getOptions,
            handleDeleteRow,
            handleOpenReport,
            handleSubmitAskForReview,
        ],
    );

    return {
        getColumnConfig,
        getTableRows,
    };
};
